import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../Config/Loader.css';
import Base1 from '../../Config/Base1';
import { fetchWithAuth } from "../../../utils/api";
import Swal from 'sweetalert2';
import { useLocation } from "react-router-dom";
import { Dropdown, Menu, Select, Table, Progress, Tag, Avatar, Tooltip } from 'antd';

const { Option } = Select;

export default function KPIDashboard() {

    const navigate = useNavigate();
    const location = useLocation();
    const [sessionUserData, setsessionUserData] = useState({});
    const [sessionActionIds, setSessionActionIds] = useState([]);
    const [modules, setModules] = useState([]);
    const [menuData, setMenuData] = useState([]);

    useEffect(() => {
        const userDataString = sessionStorage.getItem("userData");
        if (userDataString) {
            const userData = JSON.parse(userDataString);
            setsessionUserData(userData);
        } else {
            navigate("/");
        }
    }, [navigate]);

    useEffect(() => {
        const sessionMenuData = sessionStorage.getItem("menuData");
        try {
            const parsedMenu = JSON.parse(sessionMenuData);
            setMenuData(parsedMenu)
            // Find Dashboard and Visitors menus
            const dashboardMenu = parsedMenu.find(
                (item) => item.MenuName === "Dashboard"
            );

            let actionIds = [];
            if (dashboardMenu?.ActionsIds) {
                actionIds = actionIds.concat(
                    dashboardMenu.ActionsIds.split(",").map(Number)
                );
            }

            if (actionIds.length > 0) {
                // Remove duplicates just in case
                const uniqueActionIds = [...new Set(actionIds)];
                setSessionActionIds(uniqueActionIds);
            }
        } catch (err) {
            console.error("Error parsing menuData:", err);
        }
    }, []);

    const fetchMenuData = async () => {
        try {
            // First check if accessModules already exists in sessionStorage
            const sessionAccessModules = sessionStorage.getItem("accessModules");

            if (sessionAccessModules) {
                // ✅ If already cached, just parse and set directly
                setModules(JSON.parse(sessionAccessModules));
                return; // stop further execution
            }

            // 🚀 Else fetch from API
            const response = await fetchWithAuth("auth/getModules", {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            });

            if (response.ok) {
                const data = await response.json();
                const allModules = data.ResultData || [];

                // Get user access IDs
                const userData = JSON.parse(sessionStorage.getItem("userData") || "{}");
                const accessIds = userData?.AccessToModules
                    ? userData.AccessToModules.split(",").map(Number)
                    : [];

                // Filter modules by access
                const filteredModules = allModules.filter((mod) =>
                    accessIds.includes(mod.Id)
                );

                // Save to state
                setModules(filteredModules);

                // 🔥 Cache in sessionStorage for next time
                sessionStorage.setItem("accessModules", JSON.stringify(filteredModules));
            } else {
                console.error("Failed to fetch menu data:", response.statusText);
            }
        } catch (error) {
            console.error("Error fetching menu data:", error.message);
        }
    };

    const handleModuleClick = async (mod) => {
        localStorage.setItem("ModuleData", JSON.stringify(mod));

        const sessionUserData = JSON.parse(sessionStorage.getItem("userData") || "{}");
        try {
            const response = await fetchWithAuth(`auth/getmenu?OrgId=${sessionUserData.OrgId}&RoleId=${sessionUserData.RoleId}&ModuleId=${mod.Id}&UserId=${sessionUserData.Id}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            }
            );

            if (response.ok) {
                const data = await response.json();
                const menuList = data.ResultData;

                if (menuList.length > 0 && menuList[0].MenuPath) {
                    sessionStorage.setItem("menuData", JSON.stringify(menuList));
                    sessionStorage.setItem("navigationPath", menuList[0].MenuPath);
                    navigate(menuList[0].MenuPath);
                } else {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Module unavailable',
                        text: 'You don’t have access to this module yet. Please contact your administrator for help.',
                        confirmButtonText: 'OK'
                    });
                    console.warn("No menu path available.");
                }
            } else {
                Swal.fire({
                    icon: 'warning',
                    title: 'Module unavailable',
                    text: 'You don’t have access to this module yet. Please contact your administrator for help.',
                    confirmButtonText: 'OK'
                });
                console.error("Failed to fetch menu data");
            }
        } catch (error) {
            console.error("Menu fetch error:", error);
        }
    };

    useEffect(() => {
        if (sessionUserData?.OrgId) {
            fetchMenuData();
        }
    }, [sessionUserData]);

    const kpiSummaryData = [
        { title: "Total KPIs", value: "156", subtext: "Across all departments", icon: "bi-bullseye", color: "primary", progress: 88 },
        { title: "Completed", value: "128", subtext: "Successfully achieved", icon: "bi-check-circle-fill", color: "success", progress: 82 },
        { title: "Pending", value: "21", subtext: "Awaiting review", icon: "bi-hourglass-split", color: "warning", progress: 42 },
        { title: "Average Score", value: "92%", subtext: "Overall performance", icon: "bi-graph-up-arrow", color: "info", progress: 92 }
    ];

    const performanceMetrics = {
        globalScore: 92.4,
        quarterlyTrend: "+4.2% vs Last Q",
        activeObjectives: 12,
        criticalAlerts: 1
    };

    const premiumKpiData = [
        {
            id: "KPI-001",
            metricName: "Enterprise Customer Acquisition Cost (CAC)",
            owner: { name: "Sarah Jenkins", initial: "SJ", bg: "#E1F5FE", text: "#0288D1" },
            category: "Growth & Acquisition",
            targetValue: "$1,200",
            actualValue: "$1,050", // Positive variance since spending less is good
            variance: "+12.5%",
            varianceStatus: "positive",
            trend: "up",
            history: [85, 90, 95, 100],
            confidenceScore: "High"
        },
        {
            id: "KPI-002",
            metricName: "Net Revenue Retention (NRR)",
            owner: { name: "Michael Chen", initial: "MC", bg: "#E8F5E9", text: "#388E3C" },
            category: "Customer Success",
            targetValue: "115.0%",
            actualValue: "111.2%",
            variance: "-3.8%",
            varianceStatus: "negative",
            trend: "down",
            history: [60, 75, 80, 88],
            confidenceScore: "Medium"
        },
        {
            id: "KPI-003",
            metricName: "Platform Uptime & Infrastructure SLA",
            owner: { name: "DevOps Core Team", initial: "DO", bg: "#EDE7F6", text: "#5E35B1" },
            category: "Engineering",
            targetValue: "99.99%",
            actualValue: "99.95%",
            variance: "-0.04%",
            varianceStatus: "neutral",
            trend: "stable",
            history: [100, 100, 99.9, 99.95],
            confidenceScore: "High"
        },
        {
            id: "KPI-004",
            metricName: "Pipeline Velocity (Stage 1 to Closed Won)",
            owner: { name: "Amanda Ross", initial: "AR", bg: "#FFF3E0", text: "#F57C00" },
            category: "Sales Operations",
            targetValue: "18 Days",
            actualValue: "24 Days",
            variance: "-33.3%",
            varianceStatus: "critical",
            trend: "down",
            history: [40, 45, 50, 55],
            confidenceScore: "Low"
        }
    ];


    const iconColors = ['#FF6B35', '#00B8D9', '#36B37E', '#FFAB00', '#6554C0', '#FF5630'];

    return (
        <Base1>
            <div className="d-flex flex-column flex-column-fluid mb-12">
                <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                    <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                        <div className="page-title d-md-block d-none">
                            <div className="d-flex align-items-center gap-2">
                                <div className="dropdown d-inline-block me-2">
                                    <span
                                        className="menu-link bg-white shadow-sm me-2 dropdown-toggle"
                                        role="button"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                        style={{ position: "relative", zIndex: 10 }}
                                    >
                                        <span className="menu-title">
                                            <i className="fa-solid fa-grip me-1"></i> Portal
                                        </span>
                                    </span>

                                    <ul className="dropdown-menu shadow">
                                        {modules.map((mod, index) => {
                                            const isActive = location.pathname
                                                .toLowerCase()
                                                .includes(mod.ModuleName.toLowerCase());

                                            return (
                                                <li
                                                    key={index}
                                                    className={`mb-1 ${isActive ? "active-module" : ""}`}
                                                >
                                                    <a
                                                        className="dropdown-item d-flex align-items-center gap-2 module-item"
                                                        onClick={() => handleModuleClick(mod)}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <i
                                                            className={`fas fa-${mod.ImageIcon}`}
                                                            style={{
                                                                color: iconColors[index % iconColors.length],
                                                                textShadow: "1px 1px 3px rgba(0,0,0,0.5)",
                                                            }}
                                                        ></i>
                                                        <span>{mod.ModuleName}</span>
                                                    </a>
                                                </li>
                                            );
                                        })}
                                        {sessionUserData?.RoleId === 1 && (
                                            <a
                                                href={`https://betasuperportal.cooperwind.online/`}
                                                target="_blank"
                                            >
                                                <li className="dropdown-item d-flex align-items-center gap-2 module-item">
                                                    <i
                                                        className={`fa-solid fa-user-tie`}
                                                        style={{
                                                            color: "#c8e6c9",
                                                            textShadow: "1px 1px 3px rgba(0, 0, 0, 0.5)",
                                                        }}
                                                    ></i>
                                                    <span>Super Portal</span>
                                                </li>
                                            </a>
                                        )}
                                        <li
                                            className="dropdown-item d-flex align-items-center gap-2 module-item"
                                            onClick={() => navigate("/user-modules")}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <i
                                                className="fa-solid fa-arrow-left"
                                                style={{
                                                    color: "#ffccbc",
                                                    textShadow: "1px 1px 3px rgba(0,0,0,0.5)",
                                                }}
                                            ></i>
                                            <span>Go Back to Portal</span>
                                        </li>
                                    </ul>
                                </div>
                                <div className="d-flex align-items-center">
                                    {menuData?.map((menu) => {
                                        const hasSubItems = menu.SubItems && menu.SubItems.length > 0;

                                        if (hasSubItems) {
                                            const subMenu = (
                                                <Menu className="shadow-sm border-0 rounded-3 mt-2">
                                                    {menu.SubItems.map((sub) => (
                                                        <Menu.Item key={sub.AppMenuId} className="p-3">
                                                            <a
                                                                href={sub.MenuPath.startsWith('/') ? sub.MenuPath : `/${sub.MenuPath}`}
                                                                className="text-gray-700 fw-bold text-decoration-none"
                                                            >
                                                                <i className="bi bi-arrow-return-right me-1"></i>
                                                                {sub.MenuName}
                                                            </a>
                                                        </Menu.Item>
                                                    ))}
                                                </Menu>
                                            );

                                            return (
                                                <Dropdown overlay={subMenu} trigger={['hover']} key={menu.AppMenuId}>
                                                    <span className="menu-link bg-white shadow-sm me-2 cursor-pointer border border-gray-100">
                                                        <span className="menu-title">
                                                            <i className={`${menu.IconName || 'bi bi-grid'} text-primary me-2`}></i>
                                                            {menu.MenuName}
                                                        </span>
                                                        <i className="bi bi-chevron-down ms-2 fs-9"></i>
                                                    </span>
                                                </Dropdown>
                                            );
                                        }
                                        return (
                                            <a
                                                key={menu.AppMenuId}
                                                href={menu.MenuPath}
                                                style={{ position: "relative", zIndex: 10, textDecoration: 'none' }}
                                            >
                                                <span className={`menu-link bg-white shadow-sm me-2 cursor-pointer ${window.location.pathname === menu.MenuPath ? 'active border-primary' : ''}`}>
                                                    <span className="menu-title text-gray-800">
                                                        <i className={`${menu.IconName} text-primary me-2`}></i>
                                                        {menu.MenuName}
                                                    </span>
                                                </span>
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="page-title d-md-none d-block mb-3 mb-md-0">
                            <div className="d-flex align-items-center gap-2">
                                <div className="dropdown d-inline-block">
                                    <span
                                        className="menu-link bg-white shadow-sm me-2 dropdown-toggle"
                                        role="button"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                        style={{ position: "relative", zIndex: 10 }}
                                    >
                                        <span className="menu-title">
                                            <i className="fa-solid fa-grip me-1"></i>
                                        </span>
                                    </span>

                                    <ul className="dropdown-menu shadow">
                                        {modules?.map((mod, index) => {
                                            const isActive = location.pathname.toLowerCase().includes(
                                                mod.ModuleName.toLowerCase()
                                            );

                                            return (
                                                <li
                                                    key={index}
                                                    className={`mb-1 ${isActive ? "active-module" : ""}`}
                                                >
                                                    <a
                                                        className="dropdown-item d-flex align-items-center gap-2 module-item"
                                                        onClick={() => handleModuleClick(mod)}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <i
                                                            className={`fas fa-${mod.ImageIcon}`}
                                                            style={{
                                                                color: iconColors[index % iconColors.length],
                                                                textShadow: "1px 1px 3px rgba(0,0,0,0.5)",
                                                            }}
                                                        ></i>
                                                        <span>{mod.ModuleName}</span>
                                                    </a>
                                                </li>
                                            );
                                        })}
                                        {sessionUserData?.RoleId === 1 && (
                                            <a href={`https://betasuperportal.cooperwind.online/`} target="_blank">
                                                <li className="dropdown-item d-flex align-items-center gap-2 module-item">
                                                    <i
                                                        className={`fa-solid fa-user-tie`}
                                                        style={{
                                                            color: '#c8e6c9',
                                                            textShadow: '1px 1px 3px rgba(0, 0, 0, 0.5)',
                                                        }}
                                                    ></i>
                                                    <span>Super Portal</span>
                                                </li>
                                            </a>
                                        )}
                                        <li className="dropdown-item d-flex align-items-center gap-2 module-item"
                                            onClick={() => navigate("/user-modules")}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <i
                                                className="fa-solid fa-arrow-left"
                                                style={{
                                                    color: "#ffccbc",
                                                    textShadow: "1px 1px 3px rgba(0,0,0,0.5)",
                                                }}
                                            ></i>
                                            <span>Go Back to Portal</span>
                                        </li>
                                    </ul>
                                </div>
                                <a href='/edm/dashboard' style={{ position: "relative", zIndex: 10 }}>
                                    <span className="menu-link bg-white shadow-sm me-2 active">
                                        <span className="menu-title"><i className="bi bi-columns-gap text-primary fs-5"></i></span>
                                        <span className="menu-arrow"></span>
                                    </span>
                                </a>
                                <a href='/edm/documents' style={{ position: "relative", zIndex: 10 }}>
                                    <span className="menu-link bg-white shadow-sm me-2">
                                        <span className="menu-title"><i className="fa-solid fa-file-invoice fs-5"></i></span>
                                        <span className="menu-arrow"></span>
                                    </span>
                                </a>
                            </div>
                        </div>

                        <div className="d-flex align-items-center py-3 mb-2 d-none d-md-block">
                            <div className="welcome-animation">
                                <h2 className="mb-0">
                                    <span className="text-dark fw-light">Welcome, </span>
                                    <span className="animated-gradient-text">
                                        {sessionUserData?.Name || "Guest"}
                                    </span>
                                    <span className="ms-2">👋</span>
                                </h2>
                                <div
                                    className="mt-1"
                                    style={{
                                        height: '3px',
                                        width: '60px',
                                        background: '#0d6efd',
                                        borderRadius: '10px',
                                        opacity: '0.6'
                                    }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>
                <Tooltip
                    placement="bottomright"
                    mouseEnterDelay={0.2}
                    color="#1E293B"
                    overlayInnerStyle={{
                        borderRadius: "14px",
                        padding: "14px 18px",
                        boxShadow: "0 15px 40px rgba(0,0,0,.25)",
                        backdropFilter: "blur(12px)",
                        fontSize: "14px",
                        fontWeight: 500,
                        maxWidth: "320px"
                    }}
                    title={
                        <div className="d-flex align-items-start">
                            <i className="bi bi-stars text-warning fs-3 me-3 mt-1"></i>
                            <div>
                                <div className="fw-bold fs-6 text-white mb-1">Coming Soon</div>
                                <div className="text-white opacity-75">
                                    The KPI Dashboard is currently under development and will be available in an upcoming release.
                                </div>
                            </div>
                        </div>
                    }
                >
                    <div id="kt_app_content" className="app-content flex-column-fluid cursor-not-allowed" style={{ marginTop: "-30px" }}>
                        <div id="kt_app_content_container" className="app-container container-xxl">
                            <div style={{ filter: "blur(3px)", pointerEvents: "none", userSelect: "none" }}>
                                <div className="row g-5 g-xl-10">
                                    {/* --- Header Premium Stats Banner --- */}
                                    <div className="col-12 mb-2">
                                        <div className="card border-0 shadow-sm bg-light-primary 'text-primary' p-8">
                                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-4">
                                                <div>
                                                    <h3 className="fw-bolder text-gray-900 mb-1 fs-2">Corporate OKR & KPI Center</h3>
                                                    <p className="text-gray-600 fw-semibold fs-6 mb-0">
                                                        Global alignment matrix pulling from automated application performance registers.
                                                    </p>
                                                </div>
                                                <div className="d-flex align-items-center gap-6 bg-white px-6 py-4 rounded-3 shadow-xs">
                                                    <div className="text-center">
                                                        <span className="text-gray-400 fw-bold fs-8 uppercase d-block">Global Index</span>
                                                        <span className="text-gray-900 fw-bolder fs-3">{performanceMetrics.globalScore}%</span>
                                                    </div>
                                                    <div className="vr h-30px text-gray-200"></div>
                                                    <div>
                                                        <span className="badge badge-light-success fw-bold">{performanceMetrics.quarterlyTrend}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row g-5 mb-5">
                                        {kpiSummaryData.map((item, index) => (
                                            <div key={index} className="col-xl-3 col-lg-6 col-md-6">
                                                <div className={`card border-0 shadow-sm h-100 position-relative overflow-hidden bg-${item.color}-subtle dashboard-card`}>
                                                    <div className="card-body p-6">
                                                        <div className="d-flex justify-content-between align-items-start mb-5">
                                                            <div>
                                                                <span className="text-uppercase fw-bold fs-8 text-gray-600 d-block mb-2">{item.title}</span>
                                                                <h2 className="fw-bolder fs-1 text-dark mb-1">{item.value}</h2>
                                                                <span className="text-muted fw-semibold fs-7">{item.subtext}</span>
                                                            </div>
                                                            <div className={`symbol symbol-60px`}>
                                                                <div className={`symbol-label bg-${item.color}`}>
                                                                    <i className={`bi ${item.icon} fs-2 text-white`}></i>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="progress h-6px bg-light">
                                                            <div className={`progress-bar bg-${item.color}`} role="progressbar" style={{ width: `${item.progress}%` }}></div>
                                                        </div>
                                                        <div className="d-flex justify-content-between mt-3">
                                                            <span className="text-gray-500 fs-8">Progress</span>
                                                            <span className={`fw-bold text-${item.color} fs-8`}>{item.progress}%</span>
                                                        </div>
                                                    </div>
                                                    <div className={`position-absolute top-0 end-0 opacity-10`}>
                                                        <i className={`bi ${item.icon}`} style={{ fontSize: "110px" }}></i>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {/* --- Premium Grid / Row Matrix Container --- */}
                                    <div className="col-12">
                                        <div className="card card-flush shadow-sm border-0">
                                            {/* Filtering & Layout controls placeholder matching metronic theme styling */}
                                            <div className="card-header align-items-center py-5 gap-2 gap-md-5">
                                                <div className="card-title">
                                                    <div className="d-flex align-items-center position-relative my-1">
                                                        <i className="bi bi-search position-absolute ms-4 text-gray-400"></i>
                                                        <input type="text" className="form-control form-control-solid w-250px ps-12" placeholder="Search operational metrics..." />
                                                    </div>
                                                </div>
                                                <div className="card-toolbar gap-3">
                                                    <button className="btn btn-sm btn-light fw-bold">Filters</button>
                                                    <button className="btn btn-sm btn-primary fw-bold">New KPI Core Object</button>
                                                </div>
                                            </div>

                                            {/* Matrix Rows Wrapper */}
                                            <div className="card-body pt-0">
                                                {/* Custom Pure HTML Matrix Table for Custom Luxury Aesthetic Control */}
                                                <div className="table-responsive">
                                                    <table className="table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer">
                                                        <thead>
                                                            <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                                                <th className="min-w-300px">Operational KPI Object</th>
                                                                <th className="text-end min-w-100px">Target Metric</th>
                                                                <th className="text-end min-w-100px">Actual Performance</th>
                                                                <th className="text-center min-w-100px">Variance Vector</th>
                                                                <th className="min-w-150px">Target Completeness Index</th>
                                                                <th className="text-end min-w-100px">Data Integrity</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="fw-semibold text-gray-600">
                                                            {premiumKpiData.map((kpi) => {
                                                                // Calculate variance badge backgrounds
                                                                let varianceBadgeClass = "badge-light-primary text-primary";
                                                                if (kpi.varianceStatus === "positive") varianceBadgeClass = "badge-light-success text-success";
                                                                if (kpi.varianceStatus === "negative") varianceBadgeClass = "badge-light-warning text-warning";
                                                                if (kpi.varianceStatus === "critical") varianceBadgeClass = "badge-light-danger text-danger";

                                                                // Set progress colors
                                                                let progressColor = "#009EF7"; // Primary
                                                                if (kpi.varianceStatus === "positive") progressColor = "#50CD89";
                                                                if (kpi.varianceStatus === "critical") progressColor = "#F1416C";

                                                                return (
                                                                    <tr key={kpi.id}>
                                                                        {/* Metric Identification Block */}
                                                                        <td>
                                                                            <div className="d-flex align-items-center">
                                                                                {/* Avatar component with initials fallback from Ant Design inside custom bootstrap template */}
                                                                                <Tooltip title={`Owner: ${kpi.owner.name}`}>
                                                                                    <Avatar
                                                                                        style={{ backgroundColor: kpi.owner.bg, color: kpi.owner.text }}
                                                                                        className="me-3 fw-bolder shadow-xs"
                                                                                    >
                                                                                        {kpi.owner.initial}
                                                                                    </Avatar>
                                                                                </Tooltip>
                                                                                <div className="d-flex flex-column">
                                                                                    <span className="text-gray-900 text-hover-primary fw-bold mb-1 fs-6 cursor-pointer">
                                                                                        {kpi.metricName}
                                                                                    </span>
                                                                                    <span className="text-gray-400 fw-semibold fs-7">{kpi.category}</span>
                                                                                </div>
                                                                            </div>
                                                                        </td>

                                                                        {/* Target Value Column */}
                                                                        <td className="text-end font-monospace text-gray-800 fw-semibold fs-6">
                                                                            {kpi.targetValue}
                                                                        </td>

                                                                        {/* Actual Value Column */}
                                                                        <td className="text-end">
                                                                            <span className={`fw-bolder fs-6 text-gray-900 me-1`}>
                                                                                {kpi.actualValue}
                                                                            </span>
                                                                            {kpi.trend === "up" && <i className="bi bi-caret-up-fill text-success fs-8"></i>}
                                                                            {kpi.trend === "down" && <i className="bi bi-caret-down-fill text-danger fs-8"></i>}
                                                                        </td>

                                                                        {/* Variance Status Pill Badge */}
                                                                        <td className="text-center">
                                                                            <span className={`badge ${varianceBadgeClass} fw-bold fs-7 px-3 py-2`}>
                                                                                {kpi.variance}
                                                                            </span>
                                                                        </td>

                                                                        {/* Completeness Index progress block */}
                                                                        <td>
                                                                            <div className="d-flex align-items-center flex-column w-100 mt-2">
                                                                                <div className="d-flex justify-content-between w-100 mt-auto mb-1">
                                                                                    <span className="fw-semibold fs-8 text-gray-400">Yield Progress</span>
                                                                                    <span className="fw-bold fs-7 text-gray-800">
                                                                                        {kpi.history[kpi.history.length - 1]}%
                                                                                    </span>
                                                                                </div>
                                                                                <Progress
                                                                                    percent={kpi.history[kpi.history.length - 1]}
                                                                                    strokeColor={progressColor}
                                                                                    trailColor="#F4F5F7"
                                                                                    size="small"
                                                                                    showInfo={false}
                                                                                    className="m-0 w-100"
                                                                                />
                                                                            </div>
                                                                        </td>

                                                                        {/* Data Confidence / Integrity Tag */}
                                                                        <td className="text-end">
                                                                            <Tag color={kpi.confidenceScore === 'High' ? 'cyan' : kpi.confidenceScore === 'Medium' ? 'gold' : 'volcano'}>
                                                                                {kpi.confidenceScore} Sync
                                                                            </Tag>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Tooltip>
            </div>

            <style>
                {`
                    .dashboard-card{
                        border-radius:16px;
                        transition:all .3s ease;
                    }
                    .dashboard-card:hover{
                        transform:translateY(-6px);
                        box-shadow:0 1rem 3rem rgba(0,0,0,.15)!important;
                    }
                `}
            </style>
        </Base1>
    )
}